import { describeMutationError } from "@api/mutationErrors";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Dialog,
  FieldError,
  Input,
} from "@scribenote/brian-design-system";
import { RoleFieldset } from "@users/RoleFieldset";
import { useCreateUser } from "@users/userQueries";
import { useState } from "react";

const PIN_PATTERN = /^\d{4}$/;

/** Brian assigns the login code, so the new row's ID comes from the refreshed list. */
export const AddUserButton = ({ sessionKey }: { sessionKey: string }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const createUser = useCreateUser(sessionKey);

  const close = () => {
    createUser.reset();
    setName("");
    setPin("");
    setRoleIds([]);
    setOpen(false);
  };

  const trimmedName = name.trim();
  const isValid =
    trimmedName !== "" && PIN_PATTERN.test(pin) && roleIds.length > 0;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="admin-user-add"
      >
        <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
        Add user
      </Button>

      <Dialog
        open={open}
        onClose={close}
        size="sm"
        dismissable={!createUser.isPending}
        title={
          <span data-testid={open ? "admin-user-add-modal" : undefined}>
            Add a user
          </span>
        }
        footer={
          <>
            <Button
              variant="outline"
              onClick={close}
              disabled={createUser.isPending}
              data-testid="admin-user-add-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                createUser.mutate(
                  { name: trimmedName, pin, roleIds },
                  { onSuccess: close },
                )
              }
              disabled={!isValid}
              busy={createUser.isPending}
              data-testid="admin-user-add-submit"
            >
              {createUser.isPending ? "Adding" : "Add user"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={createUser.isPending}
            data-testid="admin-user-add-name"
          />
          <Input
            label="PIN"
            value={pin}
            onChange={(e) =>
              setPin(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
            inputMode="numeric"
            placeholder="4 digits"
            disabled={createUser.isPending}
            data-testid="admin-user-add-pin"
          />
          <RoleFieldset
            sessionKey={sessionKey}
            roleIds={roleIds}
            onChange={setRoleIds}
            disabled={createUser.isPending}
            testIdPrefix="admin-user-add-role"
          />
          {createUser.isError && (
            <FieldError>
              {describeMutationError(createUser.error, "add the user")}
            </FieldError>
          )}
        </div>
      </Dialog>
    </>
  );
};
